import React from 'react'
import styled from 'styled-components'
import {
  AnimatedSection,
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  fadeIn,
} from './animations/AnimatedSection'

const TakeActionSection = () => {
  return (
    <Section>
      <Container>
        {/* Left Side: Text Content */}
        <TextContent>
          <SectionHeader>
            <AnimatedSection variants={fadeInLeft}>
              <SectionLabel>TAKE ACTION</SectionLabel>
            </AnimatedSection>
            <AnimatedSection variants={fadeInLeft} delay={0.1}>
              <SectionTitle>
                Everything You Need to{' '}
                <HighlightGreen>Make a Difference</HighlightGreen>
              </SectionTitle>
            </AnimatedSection>
          </SectionHeader>
          <AnimatedSection variants={fadeInLeft} delay={0.2}>
            <SectionSubtitle>
              Join the guardian community and start your impact journey today
            </SectionSubtitle>
          </AnimatedSection>
        </TextContent>

        {/* Right Side: Visuals */}
        <VisualContent>
          {/* Main Quiz Image Container */}
          <AnimatedSection variants={fadeInRight} delay={0.1}>
            <QuizContainer>
              <QuizImage
                src="/images/homepage-new/quiz.webp"
                alt="Interactive Quizzes Dashboard"
              />
            </QuizContainer>
          </AnimatedSection>

          {/* Underneath Text for Quiz Container */}
          <AnimatedSection variants={fadeInUp} delay={0.3}>
            <QuizTextGroup>
              <QuizTitle>Interactive Quizzes</QuizTitle>
              <QuizDescription>
                Test your knowledge and learn as you go
              </QuizDescription>
            </QuizTextGroup>
          </AnimatedSection>

          {/* Floating Action Card */}
          <AnimatedSection variants={fadeIn} delay={0.4}>
            <FloatingCard>
              <FloatingImage
                src="/images/homepage-new/low-carbon.webp"
                alt="Low-Carbon Living Challenge"
              />
            </FloatingCard>
          </AnimatedSection>
        </VisualContent>
      </Container>
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 80px 24px;
  width: 100%;
  background-color: #ffffff;
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 119px;
  max-width: 1203px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 64px;
  }
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-width: 480px;

  @media (max-width: 1024px) {
    align-items: center;
    max-width: 600px;
    text-align: center;
  }
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  @media (max-width: 1024px) {
    align-items: center;
  }
`

const SectionLabel = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 16px;
  text-align: left;
  text-transform: uppercase;
  color: #607e03;
  margin: 0;

  @media (max-width: 1024px) {
    text-align: center;
  }
`

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 48px;
  text-align: left;
  line-height: 56px;
  letter-spacing: -0.04em;
  color: #000000;
  margin: 0;
  width: 100%;

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }
`

const HighlightGreen = styled.span`
  background: linear-gradient(90deg, #0e6409 0%, #bbe148 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`

const SectionSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 28px;
  color: #5e5e5e;
  margin: 0;
  text-align: left;
  width: 100%;

  @media (max-width: 1024px) {
    text-align: center;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`

const VisualContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
  min-width: 600px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    min-width: auto;
    width: 100%;
  }
`

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 24px;
`

const QuizImage = styled.img.attrs({ loading: 'lazy' })`
  width: 100%;
  height: auto;
  object-fit: contain;
`

const QuizTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
`

const QuizTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
`

const QuizDescription = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;
`

const FloatingCard = styled.div`
  position: absolute;
  left: -210px;
  bottom: 50px;
  width: 302px;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    left: 0;
    bottom: 0;
    margin-top: 24px;
    margin-bottom: 32px;
    width: 100%;
    max-width: 302px;
  }
`

const FloatingImage = styled.img.attrs({ loading: 'lazy' })`
  width: 100%;
  height: auto;
  filter: drop-shadow(4px 4px 16px rgba(0, 0, 0, 0.1));
`

export default TakeActionSection
